/**
 * "Latest Features" content — the in-app replacement for a release email.
 *
 * ── HOW TO POST AN UPDATE ────────────────────────────────────────────────────
 * Add a new entry at the TOP of RELEASE_NOTES, give it an `id` nobody has used
 * before (the date works), and deploy. Everyone who has not seen that id gets
 * the dialog once at their next login; anyone who has already read it is left
 * alone. Editing an existing entry does NOT re-show it — bump the `id` if you
 * want people to see a correction.
 *
 * Keep entries written for the person reading them, not for us: what they can
 * now do, and where. No version numbers, no internal names, no bug IDs.
 *
 * The dialog lists every entry here, so prune ones that have stopped being
 * news rather than letting the list grow forever.
 *
 * `audience` scopes an entry, and every item must be scoped to a role that can
 * actually REACH the feature. Announcing something a club manager cannot open
 * is worse than not announcing it — it reads as a missing permission.
 *
 * Admin-only areas, as enforced in App.tsx (check there before writing an
 * item — it is the source of truth): Overlays and the overlay editor, the
 * fleet stream views (/streams/live, /streams/upcoming), Facility Settings,
 * Admin Settings, Templates, Facilities, YouTube.
 *
 * Club managers reach: Schedules (all six tabs, including Ad Hoc Streams),
 * Slideshows, Reports (their own site), the Control Hub, and their own
 * facility dashboard.
 */

export type ReleaseAudience = 'all' | 'admin' | 'facility';

export interface ReleaseNote {
  /** Unique and stable. Changing it re-shows the dialog to everyone. */
  id: string;
  /** Shown to the reader, e.g. "25 September 2026". */
  date: string;
  title: string;
  audience: ReleaseAudience;
  /** One line per change. First clause says what it does, then where to find it. */
  items: Array<{ heading: string; body: string; audience?: ReleaseAudience }>;
}

export const RELEASE_NOTES: ReleaseNote[] = [
  {
    id: '2026-11-04-stream-report',
    date: '4 November 2026',
    title: 'Stream reports, with YouTube views',
    audience: 'all',
    items: [
      {
        heading: 'See your stream hours, views and sponsor air time',
        audience: 'facility',
        body:
          'Reports in the menu shows your club’s stream hours each week (league, events and ad-hoc), YouTube views, ' +
          'peak viewers and your 10 most-watched streams. It also estimates how long each sponsor image was on air ' +
          'and the views of the games it ran in. Views update overnight and keep growing as people watch replays.',
      },
      {
        heading: 'Stream hours, views and sponsor air time for every site',
        audience: 'admin',
        body:
          'Reports in the main menu shows weekly stream hours and YouTube views for all sites and for each site. ' +
          'Pick a site for its league/event/ad-hoc split, peak viewers, most-watched streams and estimated sponsor ' +
          'air time. Club managers see the same report for their own club only. A site’s views count everything ' +
          'live on its YouTube channel, including broadcasts CSM didn’t stream.',
      },
      {
        heading: 'Return-to-club checklist for Broadcast Mode',
        audience: 'admin',
        body:
          'When Broadcast Mode starts and when it ends, each host at the club shows a red-framed checklist for the ' +
          'broadcast team: cameras back to Club Presets, NDI Discovery Servers and added overlays removed, OBS on the ' +
          'CSM profile, BirdDogs on the club feed, OBS windows restored, and the Facebook group told. It closes only ' +
          'with the PIN 888888.',
      },
    ],
  },
  {
    // Admin-only: only clubs whose schedule comes from CurlOps see the option,
    // so announcing it to every club manager would read as a missing feature.
    id: '2026-10-29-curlops-events',
    date: '29 October 2026',
    title: 'Events run from CurlOps',
    audience: 'admin',
    items: [
      {
        heading: 'CurlOps can start and stop event streams',
        body:
          'At a club whose schedule comes from CurlOps, an event on the Event tab can be marked “CurlOps event” with ' +
          'its CurlOps event ID. Its games then come from CurlOps and stay in step with the bracket, each stream ' +
          'starts when Start game is pressed in CurlOps and stops at Finished, and CurlOps gets the YouTube link to ' +
          'send to followers. Starting or stopping those games from CSM needs the event’s override code (admins ' +
          'confirm instead), and every override is logged. The event must be in Event Mode for CurlOps to start games.',
      },
    ],
  },
  {
    id: '2026-10-26-event-import-review',
    date: '26 October 2026',
    title: 'Clearer event imports, and a warning before Event Mode ends',
    audience: 'all',
    items: [
      {
        heading: 'Import matches shows exactly what will change',
        body:
          'Re-importing an event’s Google Sheet now lists what’s new, what changed (old → new), what will be removed ' +
          'and what CSM keeps as it is, with unchanged games tucked away. Only those changes are made, so games you ' +
          'didn’t touch stay exactly as they were, and a draw that’s starting is never disturbed. If the sheet changes ' +
          'while the review is open, CSM shows you the new version before applying anything. Thanks to Clint at ' +
          'Frogtown for the feedback.',
      },
      {
        heading: 'The Event column can be left blank',
        body:
          'A blank Event cell now uses the event’s own name, so there’s no need to drag a name down the sheet. If ' +
          'Google Sheets has counted a name up while dragging (2026 → 2027 …), the review points it out first.',
      },
      {
        heading: 'A warning if Event Mode would end too soon',
        body:
          'When Event Mode ends, any event stream still live is stopped. If its end time comes before your last game ' +
          'is expected to finish, the Event Streams tab now shows a red warning with an Extend button, and Extend ' +
          'suggests a time that covers the last game.',
      },
    ],
  },
  {
    id: '2026-10-24-draw-start-choose',
    date: '24 October 2026',
    title: 'Choose which games to start in a draw',
    audience: 'all',
    items: [
      {
        heading: 'Start only the games you tick',
        body:
          'Start Draw Batch now starts just the games you tick, with a Select all box, so you can hold back a sheet ' +
          'that isn’t ready and start it later with its own Start button. The button tells you how many will ' +
          'start. Paused games are shown but can’t be ticked. Thanks to Clint at Frogtown for the suggestion.',
      },
      {
        heading: 'The Streaming page remembers your tab',
        body:
          'Refreshing the page, or using the back button, now returns you to the tab you were on, such as Event ' +
          'Streams, instead of Scheduled Streams.',
      },
    ],
  },
  {
    id: '2026-10-20-camera-inventory',
    date: '20 October 2026',
    title: 'Camera inventory for every site',
    audience: 'admin',
    items: [
      {
        heading: 'A record of each sheet’s cameras',
        body:
          'Facility Settings now has a Cameras tab. About once an hour the agent records which NDI camera each ' +
          'sheet’s OBS uses, plus the NDI output name and WebSocket port, and whether every camera is sending ' +
          'video. The last setup seen with every camera working is kept as the "last known good", so a rebuilt ' +
          'or mis-copied OBS can be put back. Download one site or all sites as a file.',
      },
    ],
  },
  {
    id: '2026-10-17-rule-match-fields',
    date: '17 October 2026',
    title: 'Schedule Rules can match teams and sheets',
    audience: 'all',
    items: [
      {
        heading: 'Match on event name, teams or sheet',
        body:
          'Each Schedule Rule now chooses what it matches: the event name (as before), either team’s name, ' +
          'or the sheet. For example, skip every game on sheet D, skip games where a team’s name contains ' +
          '"Juniors", or give one sheet its own game length. Sheet rules match the sheet exactly, and you can ' +
          'list several, like "A, C".',
      },
    ],
  },
  {
    id: '2026-10-17-skip-rules',
    date: '17 October 2026',
    title: 'Skip games you don’t want streamed',
    audience: 'all',
    items: [
      {
        heading: 'Skip rules',
        body:
          'Schedule Rules (Streaming → Scheduled Streams) can now skip games: set a rule’s Action to "Skip — ' +
          'don’t stream" and every game whose name contains that text stays off air. Useful for practices, ' +
          'junior sessions and private rentals that come through your schedule.',
      },
      {
        heading: 'Skipped games stay visible',
        body:
          'They remain in your schedule marked "skipped by rule", and move to the Archive the same way. To ' +
          'stream one anyway, press Resume on it. Switch the rule off and its games stream again.',
      },
    ],
  },
  {
    id: '2026-10-16-slideshow-library',
    date: '16 October 2026',
    title: 'Sponsor image library and per-image timing',
    audience: 'all',
    items: [
      {
        heading: 'Upload a sponsor once, use it in any slideshow',
        body:
          'Slideshows now have an Image Library tab holding every sponsor image your club has uploaded. ' +
          'Inside a slideshow, use "Add from Image Library" to reuse one, or upload a new image, which is ' +
          'saved to the library as well. Removing an image from a slideshow keeps it in the library.',
      },
      {
        heading: 'Set how long each image shows',
        body:
          'Each image in a slideshow now has its own duration, from 8 to 64 seconds in steps of 8, so a title ' +
          'sponsor can stay up longer than the rest. Existing images start at 8 seconds. The slideshow page ' +
          'shows how long one full pass takes.',
      },
      {
        heading: 'Slideshow changes reach the stream within seconds',
        body:
          'Adding, removing or reordering images, or changing a duration, now shows on the live overlay ' +
          'within about 5 seconds, with no need to reload anything.',
      },
      {
        heading: 'One active slideshow at a time',
        body:
          'Making a slideshow active (the star on the Slideshows page, or Set as Default) now replaces the ' +
          'previous one. Before, several could be marked active at once and only one of them actually played.',
      },
      {
        heading: 'Slideshow timing moved out of the overlay editor',
        body:
          'The slideshow widget no longer has a Cycle Duration setting; timing comes from each image in the ' +
          'slideshow itself.',
        audience: 'admin',
      },
    ],
  },
  {
    id: '2026-10-12-schedule-health',
    date: '12 October 2026',
    title: 'Clearer schedules, fewer missed games',
    audience: 'all',
    items: [
      {
        heading: 'Back-to-back games now both stream',
        body:
          'When one game ends at the same minute the next starts on that sheet, the first stream now ' +
          'ends and the next one starts. Before, the second game sometimes never started. A game held ' +
          'up by a busy sheet can also still start up to 10 minutes after its start time.',
      },
      {
        heading: 'Games that never streamed are marked Missed',
        body:
          'A game whose time passes without streaming (paused, sheet busy, computer offline) now leaves ' +
          'Scheduled Streams and appears on the Archive tab as Missed, so old games no longer pile up ' +
          'in your upcoming list. Use the Status filter on Archive to see just those.',
      },
      {
        heading: 'Know when your schedule isn’t coming through',
        body:
          'If CSM can’t read your schedule at all, a red box at the top of Scheduled Streams now says ' +
          'so. The games shown may be out of date until it’s fixed, and we’re alerted at the same ' +
          'time. Rows that need fixing at your source are still listed in the amber box.',
        audience: 'facility',
      },
      {
        heading: 'Schedule Rules for Google Sheets clubs',
        body:
          'Clubs using a Google Sheet can now add Schedule Rules too (Schedules → Scheduled Streams). ' +
          'Your sheet already gives every game an end time, so a rule needs Override ticked: it then ' +
          'replaces the sheet’s end time in CSM, and your sheet itself isn’t changed.',
        audience: 'facility',
      },
      {
        heading: 'NDI Discovery Server status on Agent Status',
        body:
          'If your broadcast computer runs your rink’s NDI Discovery Server, the service that lets ' +
          'cameras and OBS find each other, it is now restarted automatically if it stops. Agent Status ' +
          'shows whether it’s running and how many restarts there were today.',
      },
      {
        heading: 'Every schedule issue on Upcoming Streams, and a Schedules page',
        body:
          'Upcoming Streams now lists each site’s schedule issues in full, above its games. The new ' +
          'Sites With Games tile on the dashboard opens a Schedules page that groups every site: ' +
          'sync problem, has games, connected with no games, or not set up.',
        audience: 'admin',
      },
      {
        heading: 'A site can have its own Discord channel',
        body:
          'Facility Settings → Alerts takes a Discord webhook for that site. It gets copies of its ' +
          'stream-overdue, sheet-import and schedule-issue alerts, and the main channel still gets ' +
          'everything. Schedule-issue alerts are new: one message when a site’s issues change ' +
          '(new or cleared), never repeated. Overdue alerts now name the likely cause and stop at the ' +
          'game’s end. NDI Discovery restarts also post to the main channel.',
        audience: 'admin',
      },
    ],
  },
  {
    id: '2026-10-04-default-game-length',
    date: '4 October 2026',
    title: 'Set your default game length',
    audience: 'all',
    items: [
      {
        heading: 'Choose how long CSM assumes a game lasts',
        body:
          'When your schedule source doesn’t say how long a game lasts, CSM used to assume 2 hours. You can ' +
          'now set your own default at the top of Schedule Rules (Schedules → Scheduled Streams). It only ' +
          'applies to games your source gives no length and no rule matches — games with their own length, ' +
          'and your rules, are untouched. As with rules, CSM shows which upcoming games would change before ' +
          'you save. Keep it short enough that a game still ends before the next draw on the same sheet.',
      },
    ],
  },
  {
    id: '2026-09-29-adhoc-guard-rail',
    date: '29 September 2026',
    title: 'Ad-hoc streams warn before they block a game',
    audience: 'all',
    items: [
      {
        heading: 'A warning before an ad-hoc stream blocks a scheduled game',
        body:
          'A sheet streams one thing at a time, so an ad-hoc stream running when a scheduled game is due ' +
          'means that game never streams. Starting an ad-hoc stream now checks the sheet first. If a ' +
          'scheduled game would be blocked, you see which ones and choose: pause those games and start, ' +
          'start anyway, or go back and set a Duration that finishes in time. Paused games come back ' +
          'automatically as soon as the ad-hoc stream stops, as long as they haven’t started yet. This works on the website ' +
          'and on the control panel at the rink.',
      },
    ],
  },
  {
    id: '2026-09-29-schedule-rules',
    date: '29 September 2026',
    title: 'Tell CSM how long your games last',
    audience: 'all',
    items: [
      {
        heading: 'Schedule Rules set your real game lengths',
        body:
          'When your schedule source doesn’t say how long a game lasts, CSM assumes 2 hours — which can ' +
          'make two games on a sheet look like they overlap when they don’t. A Schedule Rule fixes that: ' +
          'when a game’s name contains the text you choose (say “Doubles”), it lasts the minutes you set. ' +
          'Rules are checked top to bottom and the first match wins. Before you save, CSM shows exactly ' +
          'which upcoming games would change. Find it at the top of Schedules → Scheduled Streams.',
      },
      {
        heading: 'Override a length your source gets wrong',
        body:
          'Normally a rule only fills in where your source gives no length. Tick Override on a rule and ' +
          'CSM uses your length even when the source sends one — handy when the source books a draw ' +
          'longer than it really runs.',
      },
      {
        heading: 'See which end times are estimates',
        body:
          'In the Scheduled Streams list, an end time marked “est.” is CSM’s 2-hour guess, and “rule” ' +
          'means one of your Schedule Rules set it.',
      },
    ],
  },
  {
    id: '2026-09-25-overlays-and-adhoc',
    date: '25 September 2026',
    // Titles are shown to every audience, so keep them neutral — the items
    // themselves are what get scoped. A title naming an admin-only feature
    // advertises it to club managers who cannot open it.
    title: 'September updates',
    audience: 'all',
    items: [
      {
        heading: 'Ad-hoc streams can stop themselves',
        body:
          'Starting a stream outside the schedule now offers an optional Duration in minutes. ' +
          'Fill it in and the stream stops on its own; leave it blank and nothing changes — it runs ' +
          'until you stop it. Available both on the website and on the control panel at the rink.',
      },
      {
        heading: 'Overlays can show team names from your schedule',
        audience: 'admin',
        body:
          'A text widget can now be bound to Team 1, Team 2, "Team 1 vs Team 2", the league name, ' +
          'or the draw label, and it fills itself in from whatever game is on that sheet. Between ' +
          'draws it simply shows nothing rather than the previous game. Set it in the overlay ' +
          'editor: add a text widget and pick a variable.',
      },
      {
        heading: 'Overlay edits now appear on air straight away',
        audience: 'admin',
        body:
          'Changing a widget — moving it, restyling it, rebinding it — used to leave the live output ' +
          'unchanged until someone reloaded the browser source in OBS. Edits now reach the stream ' +
          'within a few seconds. The same applies when the game changes: overlays bound to team or ' +
          'league names update at the start of each draw instead of holding the previous one.',
      },
      {
        heading: 'Copy a widget into another overlay',
        audience: 'admin',
        body:
          'Built a lower third you like? Copy it into another overlay instead of rebuilding it per ' +
          'sheet. Select the widget in the editor and use "Copy to another overlay". Colours, fonts, ' +
          'position and variable bindings all come across.',
      },
      {
        heading: 'Turn an overlay’s colours into a colour scheme',
        audience: 'admin',
        body:
          'You can now create a colour scheme from the colours already set on an overlay, instead of ' +
          'picking all of them again by hand. In the overlay editor use "Colours as scheme", or on ' +
          'the Overlays page choose "New scheme from this overlay’s colours".',
      },
      {
        heading: 'Live Now and Upcoming Streams, across every site',
        audience: 'admin',
        body:
          'The dashboard tiles are now links. "Live Now" opens everything on air right now grouped by ' +
          'site, including ad-hoc streams that have no schedule entry, and a new "Upcoming Streams" ' +
          'tile shows what is still to come today — using each site’s own local day, not UTC.',
      },
      {
        heading: 'Schedule feeds that report the wrong timezone',
        audience: 'admin',
        body:
          'A LeagueApps feed can now be told which timezone it is really reporting in, for clubs whose ' +
          'feed disagrees with their own settings. Facility Settings → Schedule → "Feed reports times ' +
          'as". Leave it on "Trust the feed" unless a mismatch has been confirmed.',
      },
    ],
  },
];

/**
 * Decides whether someone has anything new to read.
 *
 * Every id, not just the newest, because entries are listed newest-first and
 * a backfilled older entry would otherwise leave this unchanged — publishing
 * it to nobody. Any id added or removed changes the signature; editing the
 * prose of an existing entry still does not, which is the intended split.
 */
export const RELEASE_NOTES_SIGNATURE = RELEASE_NOTES.map(n => n.id).join('|');

/** Entries this role should see, with role-irrelevant items stripped. */
export function notesForRole(role: 'admin' | 'facility' | undefined): ReleaseNote[] {
  if (!role) return [];
  return RELEASE_NOTES
    .filter(n => n.audience === 'all' || n.audience === role)
    .map(n => ({ ...n, items: n.items.filter(i => !i.audience || i.audience === 'all' || i.audience === role) }))
    .filter(n => n.items.length > 0);
}
